/**
 * OAuth Integrations Component
 *
 * Central hub for managing OAuth-based external service integrations:
 * - Meeting providers (Zoom, Google Meet, Microsoft Teams, Jitsi)
 * - Calendar sync (Google Calendar, Microsoft 365)
 * - Document storage (Google Drive)
 * - Task management (Asana)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Video,
  Calendar,
  Briefcase,
  CheckSquare,
  Link,
  Unlink,
  ExternalLink,
  Check,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { oauthAPI, IntegrationStatuses, PROVIDER_INFO } from '@/services/api/oauth';
import { CloudFilesSettingsPanel } from '@/components/messaging/cloud/CloudFilesSettingsPanel';
import { MeetingsSettingsPanel } from '@/components/messaging/meetings/MeetingsSettingsPanel';
import { useTabbedChrome } from '@/components/weavein';

type ProviderKey = 'zoom' | 'google' | 'microsoft' | 'asana';

interface IntegrationCardProps {
  provider: ProviderKey;
  status: IntegrationStatuses[ProviderKey];
  onConnect: (provider: ProviderKey) => void;
  onDisconnect: (provider: ProviderKey) => void;
  isConnecting: boolean;
  isDisconnecting: boolean;
}

const providerIcons: Record<ProviderKey | 'jitsi', React.ReactNode> = {
  zoom: <Video className="w-6 h-6" />,
  google: <Calendar className="w-6 h-6" />,
  microsoft: <Briefcase className="w-6 h-6" />,
  asana: <CheckSquare className="w-6 h-6" />,
  jitsi: <Video className="w-6 h-6" />,
};

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  provider,
  status,
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
}) => {
  const info = PROVIDER_INFO[provider];
  const isLoading = isConnecting || isDisconnecting;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div
            className={`p-3 rounded-lg ${
              status.connected ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
            }`}
          >
            {providerIcons[provider]}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{info.name}</h3>
            <p className="text-sm text-gray-500">{info.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status.connected ? (
            <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-sm rounded-full">
              <Check className="w-4 h-4" />
              Connected
            </span>
          ) : (
            <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
              <X className="w-4 h-4" />
              Not connected
            </span>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex flex-wrap gap-2 mb-4">
          {info.features.map((feature) => (
            <span
              key={feature}
              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
            >
              {feature}
            </span>
          ))}
        </div>

        {status.connected && status.email && (
          <p className="text-sm text-gray-500 mb-4">
            Connected as: <span className="font-medium text-gray-700">{status.email}</span>
          </p>
        )}

        <div className="flex gap-2">
          {status.connected ? (
            <>
              <button
                onClick={() => onDisconnect(provider)}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {isDisconnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Unlink className="w-4 h-4" />
                )}
                Disconnect
              </button>
              <button
                onClick={() => onConnect(provider)}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4" />
                Reconnect
              </button>
            </>
          ) : (
            <button
              onClick={() => onConnect(provider)}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isConnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Link className="w-4 h-4" />
              )}
              Connect
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const OAuthIntegrations: React.FC = () => {
  const queryClient = useQueryClient();
  const tabbed = useTabbedChrome();
  const [connectingProvider, setConnectingProvider] = useState<ProviderKey | null>(null);
  const [disconnectingProvider, setDisconnectingProvider] = useState<ProviderKey | null>(null);

  // Fetch integration status
  const { data: status, isLoading, error, refetch } = useQuery({
    queryKey: ['integration-status'],
    queryFn: oauthAPI.getStatus,
  });

  // Connect mutation
  const connectMutation = useMutation({
    mutationFn: async (provider: ProviderKey) => {
      setConnectingProvider(provider);
      let response;
      switch (provider) {
        case 'zoom':
          response = await oauthAPI.zoom.authorize();
          break;
        case 'google':
          response = await oauthAPI.google.authorize('calendar,drive');
          break;
        case 'microsoft':
          response = await oauthAPI.microsoft.authorize();
          break;
        case 'asana':
          response = await oauthAPI.asana.authorize();
          break;
      }
      // Redirect to OAuth URL
      window.location.href = response.auth_url;
    },
    onError: () => {
      setConnectingProvider(null);
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: async (provider: ProviderKey) => {
      setDisconnectingProvider(provider);
      switch (provider) {
        case 'zoom':
          return oauthAPI.zoom.disconnect();
        case 'google':
          return oauthAPI.google.disconnect();
        case 'microsoft':
          return oauthAPI.microsoft.disconnect();
        case 'asana':
          return oauthAPI.asana.disconnect();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-status'] });
      setDisconnectingProvider(null);
    },
    onError: () => {
      setDisconnectingProvider(null);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <p className="text-gray-600">Failed to load integration status</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && <h1 className="text-2xl font-bold text-gray-900">Connected Services</h1>}
          <p className="text-gray-500 mt-1">
            Connect external services to enhance your workflow
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Meeting Providers Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Video className="w-5 h-5" />
          Video Conferencing
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <IntegrationCard
            provider="zoom"
            status={status?.zoom || { connected: false }}
            onConnect={() => connectMutation.mutate('zoom')}
            onDisconnect={() => disconnectMutation.mutate('zoom')}
            isConnecting={connectingProvider === 'zoom'}
            isDisconnecting={disconnectingProvider === 'zoom'}
          />
          {/* Jitsi Info Card (no OAuth needed) */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-100 text-green-600">
                  {providerIcons.jitsi}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {PROVIDER_INFO.jitsi.name}
                  </h3>
                  <p className="text-sm text-gray-500">{PROVIDER_INFO.jitsi.description}</p>
                </div>
              </div>
              <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                <Check className="w-4 h-4" />
                Built-in
              </span>
            </div>
            <div className="mt-4">
              <div className="flex flex-wrap gap-2 mb-4">
                {PROVIDER_INFO.jitsi.features.map((feature) => (
                  <span
                    key={feature}
                    className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>
              <p className="text-sm text-gray-500">
                Jitsi Meet is available by default. No setup required.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar & Productivity Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Calendar & Productivity
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <IntegrationCard
            provider="google"
            status={status?.google || { connected: false }}
            onConnect={() => connectMutation.mutate('google')}
            onDisconnect={() => disconnectMutation.mutate('google')}
            isConnecting={connectingProvider === 'google'}
            isDisconnecting={disconnectingProvider === 'google'}
          />
          <IntegrationCard
            provider="microsoft"
            status={status?.microsoft || { connected: false }}
            onConnect={() => connectMutation.mutate('microsoft')}
            onDisconnect={() => disconnectMutation.mutate('microsoft')}
            isConnecting={connectingProvider === 'microsoft'}
            isDisconnecting={disconnectingProvider === 'microsoft'}
          />
        </div>
      </div>

      {/* Task Management Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CheckSquare className="w-5 h-5" />
          Task Management
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <IntegrationCard
            provider="asana"
            status={status?.asana || { connected: false }}
            onConnect={() => connectMutation.mutate('asana')}
            onDisconnect={() => disconnectMutation.mutate('asana')}
            isConnecting={connectingProvider === 'asana'}
            isDisconnecting={disconnectingProvider === 'asana'}
          />
        </div>
      </div>

      {/* Cloud files for message attachments — org enablement + account restrictions (TRUS-638) */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <CloudFilesSettingsPanel />
      </div>

      {/* Meetings: guest links, link lifetime, guests per meeting (TRUS-692). Renders nothing unless the org has meetings. */}
      <div className="rounded-lg border border-border bg-background p-6 empty:hidden">
        <MeetingsSettingsPanel />
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Need Help?</h3>
        <p className="text-blue-700 dark:text-blue-300 text-sm">
          Connect your accounts above to enable calendar sync, video meetings, and task management.
          Each integration requires authorization through the provider's secure login.
        </p>
      </div>
    </div>
  );
};

export default OAuthIntegrations;
